<template>
  <div class="swept-loans">
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
      <div>
        <h1 class="mb-1 font-regular">Swept Loans</h1>
        <p class="text-muted mb-0">
          Nanoloans with completed interest and/or charges income sweeps
        </p>
      </div>
      <button class="btn btn-primary btn-sm" @click="refreshAll" :disabled="loading || statsLoading">
        {{ loading || statsLoading ? "Refreshing..." : "Refresh" }}
      </button>
    </div>

    <div class="card card--no-shadow swept-panel mb-3">
      <div class="card-body">
        <div class="row align-items-end">
          <div class="col-md-3">
            <div class="form-group mb-md-0">
              <label>Stats Period</label>
              <select class="form-control" v-model="statsPeriod" @change="onStatsPeriodChange">
                <option value="all">All time</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="this_month">This month</option>
                <option value="last_month">Last month</option>
                <option value="this_year">This year</option>
                <option value="custom">Custom dates</option>
              </select>
            </div>
          </div>
          <div class="col-md-3" v-show="statsPeriod === 'custom'">
            <div class="form-group mb-md-0">
              <label>From</label>
              <input type="date" class="form-control" v-model="startDate" @change="onDateInput" />
            </div>
          </div>
          <div class="col-md-3" v-show="statsPeriod === 'custom'">
            <div class="form-group mb-md-0">
              <label>To</label>
              <input type="date" class="form-control" v-model="endDate" @change="onDateInput" />
            </div>
          </div>
          <div class="col-md-3" v-show="statsPeriod === 'custom'">
            <div class="form-group mb-md-0">
              <label class="d-none d-md-block">&nbsp;</label>
              <button
                class="btn btn-primary btn-sm"
                @click="applyStatsDates"
                :disabled="loading || statsLoading || !startDate || !endDate"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
        <p class="text-muted small mb-0 mt-2">{{ statsRangeLabel }}</p>
      </div>
    </div>

    <div class="row">
      <div class="col-xl-3 col-md-6 mb-3" v-for="card in statCards" :key="card.key">
        <button
          type="button"
          class="swept-stat-card w-100 text-left"
          :class="{ active: type === card.filter }"
          @click="setType(card.filter)"
          :disabled="!card.filter && card.filter !== ''"
        >
          <div class="label">{{ card.label }}</div>
          <div class="value">{{ card.value }}</div>
          <div class="sub" v-if="card.sub">{{ card.sub }}</div>
        </button>
      </div>
    </div>

    <div class="card card--no-shadow swept-panel mb-3">
      <div class="card-body">
        <div class="row align-items-end">
          <div class="col-md-2">
            <div class="form-group mb-md-0">
              <label>Sweep Type</label>
              <select class="form-control" v-model="type">
                <option value="either">Interest or Charges</option>
                <option value="both">Fully Swept (Both)</option>
                <option value="interest">Interest Only</option>
                <option value="charges">Charges Only</option>
              </select>
            </div>
          </div>
          <div class="col-md-2">
            <div class="form-group mb-md-0">
              <label>Reference</label>
              <input class="form-control" v-model="ref" placeholder="Loan reference" />
            </div>
          </div>
          <div class="col-md-2">
            <div class="form-group mb-md-0">
              <label>Phone</label>
              <input class="form-control" v-model="phone" placeholder="080..." maxlength="13" />
            </div>
          </div>
          <div class="col-md-2">
            <div class="form-group mb-md-0">
              <label>BVN</label>
              <input class="form-control" v-model="bvn" placeholder="BVN" maxlength="11" />
            </div>
          </div>
          <div class="col-md-2">
            <div class="form-group mb-md-0">
              <label>From</label>
              <input type="date" class="form-control" v-model="startDate" @change="onDateInput" />
            </div>
          </div>
          <div class="col-md-2">
            <div class="form-group mb-md-0">
              <label>To</label>
              <input type="date" class="form-control" v-model="endDate" @change="onDateInput" />
            </div>
          </div>
        </div>
        <div class="d-flex justify-content-end mt-3" style="gap: 0.5rem;">
          <button class="btn btn-outline-secondary btn-sm" @click="resetFilters">Reset</button>
          <button class="btn btn-primary btn-sm" @click="loadLoans(1)" :disabled="loading">
            {{ loading ? "Loading..." : "Filter" }}
          </button>
        </div>
      </div>
    </div>

    <div class="card card--no-shadow swept-panel">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">Swept Loans ({{ total }})</h5>
        </div>

        <div v-if="loading" class="text-center text-muted py-4">Loading swept loans...</div>

        <CDataTable v-else :items="loans" :fields="fields" sorter :itemsPerPage="30">
          <template #instant_reference="{ item }">
            <td>
              <router-link
                v-if="permissions.includes('view-instant-loan')"
                :to="{ name: 'LoanComponent', params: { slug: item.instant_reference } }"
              >
                {{ item.instant_reference }}
              </router-link>
              <span v-else>{{ item.instant_reference }}</span>
            </td>
          </template>
          <template #borrower="{ item }">
            <td>
              {{ item.user ? `${item.user.first_name} ${item.user.last_name}` : "-" }}
            </td>
          </template>
          <template #phone="{ item }">
            <td>{{ item.user ? item.user.phone_number : "-" }}</td>
          </template>
          <template #approved_loan_amount="{ item }">
            <td>{{ formatMoney(item.approved_loan_amount) }}</td>
          </template>
          <template #interest_amount="{ item }">
            <td>{{ formatMoney(item.interest_amount) }}</td>
          </template>
          <template #interest_sweep_amount="{ item }">
            <td>{{ item.interest_transferred_at ? formatMoney(item.interest_sweep_amount) : "-" }}</td>
          </template>
          <template #interest_transferred_at="{ item }">
            <td>
              <span v-if="item.interest_transferred_at">{{ item.interest_transferred_at | date_two }}</span>
              <span v-else class="badge badge-warning">Pending</span>
            </td>
          </template>
          <template #charges="{ item }">
            <td>{{ formatMoney(item.charges) }}</td>
          </template>
          <template #charge_sweep_amount="{ item }">
            <td>{{ item.charges_transferred_at ? formatMoney(item.charge_sweep_amount) : "-" }}</td>
          </template>
          <template #charges_transferred_at="{ item }">
            <td>
              <span v-if="item.charges_transferred_at">{{ item.charges_transferred_at | date_two }}</span>
              <span v-else class="badge badge-warning">Pending</span>
            </td>
          </template>
          <template #sweep_status="{ item }">
            <td>
              <span class="swept-badge" :class="sweepStatusClass(item)">
                {{ sweepStatusLabel(item) }}
              </span>
            </td>
          </template>
          <template #action="{ item }">
            <td>
              <router-link
                v-if="permissions.includes('view-instant-loan')"
                class="btn btn-sm btn-primary"
                :to="{ name: 'LoanComponent', params: { slug: item.instant_reference } }"
              >
                View
              </router-link>
            </td>
          </template>
        </CDataTable>

        <div class="d-flex justify-content-end mt-3" v-if="lastPage > 1">
          <CPagination
            :pages="lastPage"
            :activePage.sync="currentPage"
            @update:activePage="onPageChange"
          ></CPagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/services/api.service";
import CDataTable from "@/components/CDataTable.vue";
import CPagination from "@/components/CPagination.vue";

const fields = [
  { key: "instant_reference", label: "Loan ID" },
  { key: "borrower", label: "Borrower" },
  { key: "phone", label: "Phone" },
  { key: "approved_loan_amount", label: "Loan Amount" },
  { key: "interest_amount", label: "Interest" },
  { key: "interest_sweep_amount", label: "Interest Swept" },
  { key: "interest_transferred_at", label: "Interest Moved At" },
  { key: "charges", label: "Charges" },
  { key: "charge_sweep_amount", label: "Charges Swept" },
  { key: "charges_transferred_at", label: "Charges Moved At" },
  { key: "sweep_status", label: "Sweep Status" },
  { key: "action", label: "Actions" }
];

export default {
  name: "SweptLoans",
  components: {
    CDataTable,
    CPagination
  },
  data() {
    return {
      fields,
      stats: null,
      statsLoading: false,
      loading: false,
      loans: [],
      type: "either",
      ref: "",
      phone: "",
      bvn: "",
      startDate: "",
      endDate: "",
      statsPeriod: "all",
      statsRange: { period: "all", from: null, to: null },
      currentPage: 1,
      lastPage: 1,
      total: 0
    };
  },
  computed: {
    permissions() {
      return this.$store.getters.permissions || [];
    },
    hasStatsRange() {
      return !!(this.statsRange && this.statsRange.from && this.statsRange.to);
    },
    statsRangeLabel() {
      if (!this.hasStatsRange) {
        return "Stats show every swept loan. Choose a period or custom dates to narrow them.";
      }
      return `Stats for ${this.formatRangeDate(this.statsRange.from)} – ${this.formatRangeDate(this.statsRange.to)}. The loan list uses the same window.`;
    },
    statCards() {
      const s = this.stats || {};
      return [
        {
          key: "either",
          label: "Loans Swept",
          value: this.formatNumber(s.either_swept_loans),
          sub: this.hasStatsRange
            ? `${this.formatRangeDate(this.statsRange.from)} – ${this.formatRangeDate(this.statsRange.to)}`
            : `${this.formatNumber(s.swept_today)} today`,
          filter: "either"
        },
        {
          key: "fully",
          label: "Fully Swept",
          value: this.formatNumber(s.fully_swept_loans),
          sub: "Interest + charges done",
          filter: "both"
        },
        {
          key: "interest_amount",
          label: "Interest Swept",
          value: this.formatMoney(s.total_interest_swept),
          sub: `${this.formatNumber(s.interest_swept_loans)} loans`,
          filter: "interest"
        },
        {
          key: "charges_amount",
          label: "Charges Swept",
          value: this.formatMoney(s.total_charges_swept),
          sub: `${this.formatNumber(s.charges_swept_loans)} loans`,
          filter: "charges"
        },
        {
          key: "total_income",
          label: "Total Income Swept",
          value: this.formatMoney(s.total_income_swept),
          sub: "Interest + charges",
          filter: "either"
        },
        {
          key: "pending_interest",
          label: "Pending Interest Legs",
          value: this.formatNumber(s.pending_interest_legs),
          sub: this.hasStatsRange ? "Eligible in this period, not moved" : "Eligible, not moved",
          filter: ""
        },
        {
          key: "pending_charges",
          label: "Pending Charges Legs",
          value: this.formatNumber(s.pending_charges_legs),
          sub: this.hasStatsRange ? "Eligible in this period, not moved" : "Eligible, not moved",
          filter: ""
        },
        {
          key: "interest_loans",
          label: "Interest Legs Done",
          value: this.formatNumber(s.interest_swept_loans),
          sub: "Completed interest transfers",
          filter: "interest"
        }
      ];
    }
  },
  methods: {
    formatMoney(value) {
      const n = Number(value || 0);
      return `₦${n.toLocaleString("en-NG", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      })}`;
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("en-NG");
    },
    formatRangeDate(iso) {
      if (!iso) return "";
      const parts = String(iso).split("-");
      if (parts.length !== 3) return iso;
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const month = months[Number(parts[1]) - 1] || parts[1];
      return `${parts[2]} ${month} ${parts[0]}`;
    },
    onStatsPeriodChange() {
      if (this.statsPeriod === "custom") return;
      this.startDate = "";
      this.endDate = "";
      this.loadLoans(1);
    },
    onDateInput() {
      this.statsPeriod = this.startDate || this.endDate ? "custom" : "all";
    },
    applyStatsDates() {
      if (!this.startDate || !this.endDate) return;
      this.statsPeriod = "custom";
      this.loadLoans(1);
    },
    sweepStatusLabel(item) {
      const interestDone = !!item.interest_transferred_at;
      const chargesDone = !!item.charges_transferred_at;
      if (interestDone && chargesDone) return "Fully swept";
      if (interestDone) return "Interest only";
      if (chargesDone) return "Charges only";
      return "Not swept";
    },
    sweepStatusClass(item) {
      const interestDone = !!item.interest_transferred_at;
      const chargesDone = !!item.charges_transferred_at;
      if (interestDone && chargesDone) return "is-full";
      if (interestDone || chargesDone) return "is-partial";
      return "is-none";
    },
    setType(filter) {
      if (!filter) return;
      this.type = filter;
      this.loadLoans(1);
    },
    resetFilters() {
      this.type = "either";
      this.ref = "";
      this.phone = "";
      this.bvn = "";
      this.startDate = "";
      this.endDate = "";
      this.statsPeriod = "all";
      this.loadLoans(1);
    },
    refreshAll() {
      this.loadLoans(this.currentPage || 1);
    },
    async loadLoans(page = 1) {
      this.loading = true;
      this.statsLoading = true;
      this.currentPage = page;
      if (this.statsPeriod === "all" && this.startDate && this.endDate) {
        this.statsPeriod = "custom";
      }
      const requestId = (this.loadRequestId || 0) + 1;
      this.loadRequestId = requestId;
      try {
        const params = [
          `page=${page}`,
          `type=${encodeURIComponent(this.type)}`,
          `instant_reference=${encodeURIComponent(this.ref || "")}`,
          `phone=${encodeURIComponent(this.phone || "")}`,
          `bvn=${encodeURIComponent(this.bvn || "")}`,
          `stats_period=${encodeURIComponent(this.statsPeriod || "all")}`,
          `start_date=${encodeURIComponent(this.startDate || "")}`,
          `end_date=${encodeURIComponent(this.endDate || "")}`,
          `stats_start_date=${encodeURIComponent(this.startDate || "")}`,
          `stats_end_date=${encodeURIComponent(this.endDate || "")}`
        ].join("&");

        const response = await ApiService.get(`loan/instant/swept-loans?${params}`);
        if (requestId !== this.loadRequestId) return;
        const payload = response.data.data || {};
        this.stats = payload.stats || null;
        const range = payload.range || { period: this.statsPeriod || "all", from: null, to: null };
        this.statsRange = range;
        if (range.from && range.to) {
          this.startDate = range.from;
          this.endDate = range.to;
        }
        if (range.period) {
          this.statsPeriod = range.period;
        }
        this.loans = payload.data || [];
        this.currentPage = payload.current_page || 1;
        this.lastPage = payload.last_page || 1;
        this.total = payload.total || this.loans.length;
      } catch (error) {
        if (requestId !== this.loadRequestId) return;
        console.log(error && error.response);
        this.loans = [];
      } finally {
        if (requestId === this.loadRequestId) {
          this.loading = false;
          this.statsLoading = false;
        }
      }
    },
    onPageChange(page) {
      if (page && this.currentPage !== page) {
        this.loadLoans(page);
      }
    }
  },
  mounted() {
    this.loadLoans(1);
  }
};
</script>

<style scoped>
.swept-loans {
  color: #1f2937;
}

.swept-stat-card {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 14px;
  padding: 1rem 1.1rem;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  min-height: 110px;
}

.swept-stat-card:hover:not(:disabled),
.swept-stat-card.active {
  border-color: #93c5fd;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.08);
}

.swept-stat-card:disabled {
  cursor: default;
  opacity: 1;
}

.swept-stat-card .label {
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 0.35rem;
}

.swept-stat-card .value {
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.2;
}

.swept-stat-card .sub {
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 0.2rem;
}

.swept-panel {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
}

.swept-badge {
  display: inline-block;
  border-radius: 999px;
  padding: 0.2rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 600;
}

.swept-badge.is-full {
  background: #dcfce7;
  color: #166534;
}

.swept-badge.is-partial {
  background: #fef3c7;
  color: #92400e;
}

.swept-badge.is-none {
  background: #f3f4f6;
  color: #374151;
}
</style>
